if (!customElements.get('announcement-ticker')) {
  customElements.define('announcement-ticker', class extends HTMLElement {
    connectedCallback() {
      this.messages = this.querySelector('[data-ticker-messages]');
      this.track = this.querySelector('[data-ticker-track]');
      this.viewport = this.querySelector('.announcement-ticker__viewport');
      this.pauseButton = this.querySelector('[data-announcement-pause]');
      this.countdowns = [...this.messages.querySelectorAll('announcement-countdown')];
      this.speed = Math.min(100, Math.max(10, Number(this.dataset.speed) || 35));
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches || Boolean(window.Shopify?.designMode) || this.matches(':focus-within');
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.layoutObserver = new ResizeObserver(() => this.scheduleLayout());
      this.layoutObserver.observe(this.viewport);
      this.heightObserver = new ResizeObserver(() => this.updateHeight());
      this.heightObserver.observe(this);
      this.addEventListener('announcement:countdown', event => {
        if (event.detail.expired) this.buildTrack();
        else this.syncCountdown(event.target);
      }, options);
      this.addEventListener('focusin', event => {
        if (this.track.contains(event.target)) return;
        this.paused = true;
        this.updateState();
      }, options);
      // Keep duplicate links out of pointer focus as well as the keyboard tab order.
      // Native link clicks, modifier clicks and context menus retain their destinations.
      this.track.addEventListener('mousedown', event => {
        if (event.target.closest('a')) event.preventDefault();
      }, options);
      this.pauseButton?.addEventListener('pointerdown', () => { this.pointerPaused = this.paused; }, options);
      this.pauseButton?.addEventListener('pointercancel', () => { this.pointerPaused = undefined; }, options);
      this.pauseButton?.addEventListener('click', event => {
        const wasPaused = event.detail > 0 ? this.pointerPaused ?? this.paused : this.paused;
        this.pointerPaused = undefined;
        this.paused = !wasPaused;
        this.updateState();
      }, options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.updateState();
      }, options);
      document.addEventListener('visibilitychange', () => { this.dataset.background = String(document.hidden); }, options);
      this.closest('.shopify-section')?.addEventListener('shopify:section:select', () => {
        this.paused = true;
        this.updateState();
      }, options);
      this.buildTrack();
      this.updateState();
    }

    buildTrack() {
      if (this.sequence) this.layoutObserver.unobserve(this.sequence);
      this.track.replaceChildren();
      this.group = document.createElement('div');
      this.group.className = 'announcement-ticker__group';
      this.sequence = document.createElement('ul');
      this.sequence.className = 'announcement-ticker__sequence';
      this.messages.querySelectorAll('[data-announcement-item]:not([data-announcement-expired])').forEach(item => {
        const copy = item.cloneNode(true);
        copy.hidden = false;
        copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
        copy.querySelectorAll('a').forEach(link => { link.tabIndex = -1; });
        copy.querySelectorAll('announcement-countdown').forEach(countdown => {
          const source = item.querySelector('announcement-countdown');
          const span = document.createElement('span');
          span.className = countdown.className;
          span.dataset.countdownCopy = this.countdowns.indexOf(source);
          span.innerHTML = countdown.innerHTML;
          countdown.replaceWith(span);
        });
        this.sequence.append(copy);
      });
      this.empty = !this.sequence.children.length;
      this.hidden = this.empty;
      this.group.append(this.sequence);
      this.track.append(this.group);
      this.layoutObserver.observe(this.sequence);
      this.sequenceWidth = 0;
      this.repetitions = 0;
      this.layout();
      this.updateHeight();
    }

    syncCountdown(source) {
      const index = this.countdowns.indexOf(source);
      this.track.querySelectorAll(`[data-countdown-copy="${index}"]`).forEach(copy => {
        copy.innerHTML = source.innerHTML;
      });
    }

    scheduleLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => this.layout());
    }

    layout() {
      if (this.empty) return;
      const sequenceWidth = this.sequence.getBoundingClientRect().width;
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      if (!sequenceWidth || !viewportWidth) return;
      const repetitions = Math.max(1, Math.ceil(viewportWidth / sequenceWidth));
      if (this.sequenceWidth === sequenceWidth && this.repetitions === repetitions) return;
      this.sequenceWidth = sequenceWidth;
      this.repetitions = repetitions;
      this.group.replaceChildren(this.sequence);
      for (let i = 1; i < repetitions; i++) this.group.append(this.sequence.cloneNode(true));
      this.track.replaceChildren(this.group, this.group.cloneNode(true));
      this.track.style.animationDuration = `${sequenceWidth * repetitions / this.speed}s`;
      this.viewport.scrollLeft = 0;
      this.dataset.ready = 'true';
      this.messages.removeAttribute('tabindex');
      if (this.pauseButton) this.pauseButton.hidden = this.motion.matches;
    }

    updateState() {
      this.dataset.paused = String(this.paused || this.motion.matches);
      this.dataset.background = String(document.hidden);
      this.countdowns.forEach(countdown => countdown.setPaused?.(this.paused || this.motion.matches));
      if (this.pauseButton) {
        const label = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
        this.pauseButton.setAttribute('aria-label', label);
        this.pauseButton.title = label;
        this.pauseButton.dataset.paused = String(this.paused);
        this.pauseButton.hidden = this.motion.matches || !this.dataset.ready || this.empty;
      }
      this.updateHeight();
    }

    updateHeight() {
      const bar = this.closest('.announcement-bar');
      if (!bar) return;
      const height = bar.dataset.sticky === 'true' ? bar.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.layoutObserver?.disconnect();
      this.heightObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      document.documentElement.style.removeProperty('--announcement-height');
    }
  });
}
